import React from 'react';
import { toast } from 'react-toastify';
import { CheckCircle, Loader2, Plus, Trash2, Upload, FileText, AlertCircle, X } from 'lucide-react';
import i18n from '../../../../../i18n';
import { auctionApprove } from '../services/auctionApprovalServices';
import { CustomModal } from '../../../../common/CustomModal';
import '../../../../../styles/components/action-modal.scss';
import { getLoggedUser } from '../../../../../state/user';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const PREVIEW_URL_KEY = {
  file_receipt: 'receiptPreviewUrl',
  invoice: 'invoicePreviewUrl',
};

const INPUT_ID = {
  file_receipt: 'aam-file-receipt',
  invoice: 'aam-invoice',
};

const user = getLoggedUser();

export class AuctionApproveModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      is_loading: false,
      input: {},
      errors: {},
      fileInputs: [],
      file_receipt: null,
      invoice: null,
      receiptPreviewUrl: null,
      invoicePreviewUrl: null,
      dragOver: { file_receipt: false, invoice: false },
      split_vat_enabled: false,
    };

    this.clientNameRef = React.createRef();
    this.bankNameRef = React.createRef();
    this.accountNumberRef = React.createRef();
    this.paymentDateRef = React.createRef();
    this.fileReceiptRef = React.createRef();
    this.invoiceRef = React.createRef();

    this.fieldRefMap = {
      client_name: this.clientNameRef,
      bank_name: this.bankNameRef,
      account_number: this.accountNumberRef,
      payment_date: this.paymentDateRef,
      file_receipt: this.fileReceiptRef,
      invoice: this.invoiceRef,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentWillUnmount() {
    this.revokePreviewUrl('receiptPreviewUrl');
    this.revokePreviewUrl('invoicePreviewUrl');
  }

  revokePreviewUrl = (previewUrlKey) => {
    if (this.state[previewUrlKey]) {
      URL.revokeObjectURL(this.state[previewUrlKey]);
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      input: { ...prevState.input, [name]: value },
      errors: { ...prevState.errors, [name]: '' },
    }));
  };

  formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return i18n.t('invalid_file_type') || 'Only JPG, PNG or PDF files are allowed';
    }
    if (file.size > MAX_FILE_SIZE) {
      return i18n.t('file_too_large') || 'File size must not exceed 5 MB';
    }
    return '';
  };

  setFileForField = (field, file) => {
    if (!file) return;

    const error = this.validateFile(file);
    const previewUrlKey = PREVIEW_URL_KEY[field];

    if (error) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, [field]: error },
      }));
      return;
    }

    this.revokePreviewUrl(previewUrlKey);
    const isImageFile = file.type.startsWith('image/');
    const newPreviewUrl = isImageFile ? URL.createObjectURL(file) : null;

    this.setState((prevState) => ({
      [field]: file,
      [previewUrlKey]: newPreviewUrl,
      errors: { ...prevState.errors, [field]: '' },
    }));
  };

  handleFileInputChange = (field) => (e) => {
    const file = e.target.files?.[0] || null;
    this.setFileForField(field, file);
    e.target.value = '';
  };

  handleDragOver = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.is_loading) return;
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: true } }));
  };

  handleDragLeave = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: false } }));
  };

  handleDrop = (field) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({ dragOver: { ...prevState.dragOver, [field]: false } }));
    if (this.state.is_loading) return;
    const file = e.dataTransfer.files?.[0] || null;
    this.setFileForField(field, file);
  };

  removeFile = (field) => () => {
    const previewUrlKey = PREVIEW_URL_KEY[field];
    this.revokePreviewUrl(previewUrlKey);
    this.setState((prevState) => ({
      [field]: null,
      [previewUrlKey]: null,
      errors: { ...prevState.errors, [field]: '' },
    }));
  };

  // ---- additional (non-required) attachments ----

  onAdditionalFileChange = (e, index) => {
    const updatedInputs = [...this.state.fileInputs];
    updatedInputs[index].file = e.target.files[0];
    this.setState({ fileInputs: updatedInputs });
  };

  addNewFileInput = () => {
    this.setState((prevState) => ({
      fileInputs: [...prevState.fileInputs, { id: prevState.fileInputs.length + 1, file: null }],
    }));
  };

  removeAdditionalFile = (index) => {
    this.setState((prevState) => ({
      fileInputs: prevState.fileInputs.filter((_, i) => i !== index),
    }));
  };

  // ---- validation ----

  /** Returns a field -> message error object. Empty object means valid.
   * NOTE: payment_date is only required for non-client roles when
   * isClientEnrollment is true, matching the fact that the payment_date
   * field itself is only ever rendered for non-client roles — the
   * previous version required it unconditionally in the isClientEnrollment
   * branch (even for role C), which made the form permanently
   * un-submittable for a client on a client-enrollment auction, since
   * there was no visible field for them to satisfy it. See note at the
   * bottom of this response before relying on this fix. */
  validateOfflineForm = () => {
    const { isClientEnrollment } = this.props;
    const isClient = user.role === 'C';
    const { input, file_receipt, invoice } = this.state;
    const errors = {};

    if (isClientEnrollment) {
      if (!isClient && !input.payment_date) {
        errors.payment_date = i18n.t('payment_date_is_required') || 'Payment date is required';
      }
      if (!file_receipt) {
        errors.file_receipt = i18n.t('receipt_is_required') || 'Receipt file is required';
      }
      return errors;
    }

    if (!input.client_name) {
      errors.client_name = i18n.t('client_name_is_required') || 'This field is required';
    }
    if (!input.bank_name) {
      errors.bank_name = i18n.t('bank_name_is_required') || 'Bank name is required';
    }
    if (!input.account_number) {
      errors.account_number = i18n.t('account_number_is_required') || 'Account number is required';
    }
    if (!isClient && !input.payment_date) {
      errors.payment_date = i18n.t('payment_date_is_required') || 'Payment date is required';
    }
    if (!file_receipt) {
      errors.file_receipt = i18n.t('receipt_is_required') || 'Receipt file is required';
    }
    if (!invoice) {
      errors.invoice = i18n.t('invoice_is_required') || 'Invoice is required';
    }

    return errors;
  };

  focusFirstError = (errors) => {
    const order = ['client_name', 'bank_name', 'account_number', 'payment_date', 'file_receipt', 'invoice'];
    const firstKey = order.find((key) => errors[key]);
    if (firstKey) {
      this.fieldRefMap[firstKey]?.current?.focus?.();
      this.fieldRefMap[firstKey]?.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  async handleSubmit(event) {
    event.preventDefault();
    const { input, fileInputs } = this.state;
    const { selectedIds, groupId, isClientEnrollment } = this.props;

    const errors = this.validateOfflineForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      this.focusFirstError(errors);
      return;
    }

    try {
      this.setState({ is_loading: true });
      const formData = new FormData();

      selectedIds.forEach((id, index) => {
        formData.append(`selected_ids[${index}]`, id);
      });
      formData.append('is_client_enrollment', isClientEnrollment ? 1 : 0);
      formData.append('group_id', groupId);
      formData.append('remarks', input.remarks || '');

      if (input.payment_date) {
        formData.append('payment_date', input.payment_date);
      }

      if (!isClientEnrollment) {
        formData.append('client_name', input.client_name);
        formData.append('bank_name', input.bank_name);
        formData.append('account_number', input.account_number);
        formData.append('vat_account_status', this.state.split_vat_enabled ? 1 : 0);
        formData.append('vat_account_number', input.vat_account_number);
      }

      if (this.state.file_receipt) {
        formData.append('attachment', this.state.file_receipt, this.state.file_receipt.name);
      }
      if (!isClientEnrollment && this.state.invoice) {
        formData.append('invoice', this.state.invoice, this.state.invoice.name);
      }

      if (fileInputs.length > 0) {
        fileInputs.forEach((entry, index) => {
          if (entry.file) {
            formData.append(`additional_attachments${index}`, entry.file, entry.file.name);
          }
        });
      }

      const response = await auctionApprove(formData);
      if (response && response.success) {
        toast.success(i18n.t('auction_approved') || 'Auction approved successfully');
        this.setState({ is_loading: false });
        if (this.props.onSuccess) {
          this.props.onSuccess();
        } else {
          this.props.onClose();
        }
      } else {
        toast.error(response?.message || i18n.t('something_went_wrong'));
        this.setState({ is_loading: false });
      }
    } catch (err) {
      console.error(err);
      toast.error(i18n.t('something_went_wrong'));
      this.setState({ is_loading: false });
    }
  }

  // ---- required single-file dropzone (receipt / invoice) ----

  renderFileUploadField = (field, label, required) => {
    const { is_loading, errors, dragOver } = this.state;
    const file = this.state[field];
    const isDraggingOver = dragOver[field];
    const inputId = INPUT_ID[field];
    const fieldRef = this.fieldRefMap[field];
    const previewUrl = this.state[PREVIEW_URL_KEY[field]];

    const isImage = file?.type?.startsWith('image/');
    const isPdf = file?.type === 'application/pdf';

    return (
      <div className="wam-form-group wam-form-group--file">
        <label className="wam-form-label" htmlFor={inputId}>
          {label} {required && <span className="wam-required-mark">*</span>}
        </label>

        <input
          type="file"
          id={inputId}
          name={field}
          accept=".jpg,.jpeg,.png,.pdf"
          style={{ display: 'none' }}
          disabled={is_loading}
          onChange={this.handleFileInputChange(field)}
        />

        {!file ? (
          <div
            className={`wam-upload-zone ${isDraggingOver ? 'wam-upload-zone--dragover' : ''} ${
              is_loading ? 'wam-upload-zone--disabled' : ''
            } ${errors[field] ? 'wam-upload-zone--error' : ''}`}
            onDragOver={this.handleDragOver(field)}
            onDragLeave={this.handleDragLeave(field)}
            onDrop={this.handleDrop(field)}
          >
            <div className="wam-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="wam-upload-zone__title">{i18n.t('drop_file_here') || 'Drop file here'}</p>
            <p className="wam-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
            <button
              type="button"
              ref={fieldRef}
              className="wam-upload-zone__btn"
              disabled={is_loading}
              onClick={() => document.getElementById(inputId).click()}
            >
              {i18n.t('browse_file') || 'Browse File'}
            </button>
          </div>
        ) : (
          <div className="wam-receipt-preview">
            <div className="wam-receipt-preview__header">
              <FileText size={15} className="wam-receipt-preview__file-icon" />
              <span className="wam-receipt-preview__name">{file.name}</span>
              <span className="wam-receipt-preview__size">{this.formatFileSize(file.size)}</span>
              <button
                type="button"
                className="wam-receipt-preview__remove"
                onClick={this.removeFile(field)}
                disabled={is_loading}
              >
                <X size={12} /> {i18n.t('remove') || 'Remove'}
              </button>
            </div>

            <div className={`wam-receipt-preview__body ${isImage ? 'wam-receipt-preview__body--image' : ''}`}>
              {isImage && previewUrl && (
                <img src={previewUrl} alt="File preview" className="wam-receipt-preview__img" />
              )}
              {isPdf && (
                <div className="wam-receipt-preview__pdf">
                  <FileText size={32} className="wam-receipt-preview__pdf-icon" />
                  <div>
                    <p className="wam-receipt-preview__pdf-name">{file.name}</p>
                    <span className="wam-receipt-preview__pdf-meta">
                      PDF document · {this.formatFileSize(file.size)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="wam-receipt-preview__actions">
              <button
                type="button"
                className="wam-receipt-preview__change-btn"
                onClick={() => document.getElementById(inputId).click()}
                disabled={is_loading}
              >
                <Upload size={13} /> {i18n.t('change_file') || 'Change File'}
              </button>
            </div>
          </div>
        )}

        {errors[field] && (
          <div id={`${inputId}-error`} className="wam-upload-error">
            <AlertCircle size={13} />
            {errors[field]}
          </div>
        )}
      </div>
    );
  };

  // ---- optional additional attachments (dynamic list) ----

  renderAdditionalAttachments = () => {
    const { fileInputs, is_loading } = this.state;

    return (
      <div className="wam-form-group wam-form-group--file">
        <label className="wam-form-label">{i18n.t('additional_doc')}</label>

        {fileInputs.map((entry, index) => {
          const inputId = `aam-additional-${entry.id}`;
          return (
            <div key={entry.id} className="wam-receipt-preview wam-receipt-preview--compact">
              <input
                type="file"
                id={inputId}
                style={{ display: 'none' }}
                disabled={is_loading}
                onChange={(e) => this.onAdditionalFileChange(e, index)}
              />
              <div className="wam-receipt-preview__header">
                <FileText size={15} className="wam-receipt-preview__file-icon" />
                <span className="wam-receipt-preview__name">
                  {entry.file ? entry.file.name : i18n.t('choose_file') || 'No file selected'}
                </span>
                {entry.file && (
                  <span className="wam-receipt-preview__size">{this.formatFileSize(entry.file.size)}</span>
                )}
                <button
                  type="button"
                  className="wam-receipt-preview__change-btn"
                  onClick={() => document.getElementById(inputId).click()}
                  disabled={is_loading}
                >
                  <Upload size={13} /> {i18n.t('choose_file') || 'Choose'}
                </button>
                <button
                  type="button"
                  className="wam-receipt-preview__remove"
                  onClick={() => this.removeAdditionalFile(index)}
                  disabled={is_loading}
                >
                  <Trash2 size={12} /> {i18n.t('remove') || 'Remove'}
                </button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          className="wam-upload-zone__btn wam-upload-zone__btn--ghost"
          onClick={this.addNewFileInput}
          disabled={is_loading}
        >
          <Plus size={16} /> {i18n.t('add_new')}
        </button>
      </div>
    );
  };

  renderOfflineForm = () => {
    const { input, errors, is_loading } = this.state;
    const { isClientEnrollment, user } = this.props;
    const isClient = user.role === 'C';

    return (
      <div className="wam-form-grid">
        {!isClientEnrollment && (
          <>
            <div className="wam-form-group">
              <label className="wam-form-label" htmlFor="aam-client-name">
                {isClient ? i18n.t('beneficiary_name') : i18n.t('client_name')}{' '}
                <span className="wam-required-mark">*</span>
              </label>
              <input
                id="aam-client-name"
                ref={this.clientNameRef}
                type="text"
                name="client_name"
                value={input.client_name || ''}
                onChange={this.handleChange}
                className={`wam-form-input ${errors.client_name ? 'wam-form-input--error' : ''}`}
                placeholder={isClient ? i18n.t('please_enter_beneficiary_name') : i18n.t('please_enter_client_name')}
                disabled={is_loading}
                aria-invalid={!!errors.client_name}
              />
              {errors.client_name && <span className="wam-field-error">{errors.client_name}</span>}
            </div>

            <div className="wam-form-group">
              <label className="wam-form-label" htmlFor="aam-bank-name">
                {i18n.t('bank_name')} <span className="wam-required-mark">*</span>
              </label>
              <input
                id="aam-bank-name"
                ref={this.bankNameRef}
                type="text"
                name="bank_name"
                value={input.bank_name || ''}
                onChange={this.handleChange}
                className={`wam-form-input ${errors.bank_name ? 'wam-form-input--error' : ''}`}
                placeholder={i18n.t('bank_name')}
                disabled={is_loading}
                aria-invalid={!!errors.bank_name}
              />
              {errors.bank_name && <span className="wam-field-error">{errors.bank_name}</span>}
            </div>

            <div className="wam-form-group">
              <label className="wam-form-label" htmlFor="aam-account-number">
                {i18n.t('account_number')} <span className="wam-required-mark">*</span>
              </label>
              <input
                id="aam-account-number"
                ref={this.accountNumberRef}
                type="text"
                name="account_number"
                value={input.account_number || ''}
                onChange={this.handleChange}
                className={`wam-form-input ${errors.account_number ? 'wam-form-input--error' : ''}`}
                placeholder={i18n.t('enter_account_number')}
                disabled={is_loading}
                aria-invalid={!!errors.account_number}
              />
              {errors.account_number && <span className="wam-field-error">{errors.account_number}</span>}
            </div>

            <div className="wam-form-group">
              <label className="wallet-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="split_vat_enabled"
                  name="split_vat_enabled"
                  checked={this.state.split_vat_enabled}
                  onChange={(e) => this.setState({ split_vat_enabled: e.target.checked })}
                  disabled={is_loading}
                />
                <span className="custom-checkbox"></span>
                <span className="wam-form-label">{i18n.t('split_vat_id_enabled')}</span>
              </label>
            </div>

            {this.state.split_vat_enabled && (
              <div className="wam-form-group">
                <label className="wam-form-label" htmlFor="aam-vat-account-number">
                  {i18n.t('vat_account_number')}
                </label>
                <input
                  id="aam-vat-account-number"
                  type="text"
                  name="vat_account_number"
                  value={input.vat_account_number || ''}
                  onChange={this.handleChange}
                  className="wam-form-input"
                  placeholder={i18n.t('enter_vat_account_number')}
                  disabled={is_loading}
                />
              </div>
            )}
          </>
        )}
        {!isClient && (
          <div className="wam-form-group">
            <label className="wam-form-label" htmlFor="aam-payment-date">
              {i18n.t('final_due_date')} {!isClient && <span className="wam-required-mark">*</span>}
            </label>
            <input
              id="aam-payment-date"
              ref={this.paymentDateRef}
              type="date"
              name="payment_date"
              value={input.payment_date || ''}
              onChange={this.handleChange}
              className={`wam-form-input ${errors.payment_date ? 'wam-form-input--error' : ''}`}
              disabled={is_loading}
              aria-invalid={!!errors.payment_date}
            />
            {errors.payment_date && <span className="wam-field-error">{errors.payment_date}</span>}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { show, onClose, isClientEnrollment } = this.props;
    const { is_loading, input } = this.state;

    return (
      <CustomModal isOpen={show} header={i18n.t('auction_approval')} onClose={onClose}>
        <div className="action-modal-container">
          <form className="wam-form-section" autoComplete="off">
            {this.renderOfflineForm()}

            {this.renderFileUploadField('file_receipt', i18n.t('attachment') || 'Attachment', true)}
            {!isClientEnrollment &&
              this.renderFileUploadField('invoice', i18n.t('clinet_invoice') || 'Client Invoice', true)}

            {this.renderAdditionalAttachments()}

            <div className="wam-form-group">
              <label className="wam-form-label" htmlFor="aam-remarks">
                {i18n.t('remarks')}
              </label>
              <textarea
                id="aam-remarks"
                name="remarks"
                value={input.remarks || ''}
                onChange={this.handleChange}
                rows={4}
                className="wam-form-input"
                placeholder={i18n.t('enter_remarks')}
                disabled={is_loading}
              />
            </div>

            <div className="action-buttons-container">
              <button type="button" className="modal-btn btn-cancel" onClick={onClose} disabled={is_loading}>
                {i18n.t('cancel')}
              </button>
              <button type="button" className="modal-btn btn-success" onClick={this.handleSubmit} disabled={is_loading}>
                <CheckCircle size={18} />
                {is_loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> {i18n.t('approve')}
                  </>
                ) : (
                  i18n.t('approve')
                )}
              </button>
            </div>
          </form>
        </div>
      </CustomModal>
    );
  }
}
